import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Bell, User, Menu, X, Baby } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import logo from '../assets/Logo.png';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const Navbar = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isLoggedIn, currentUser, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications from database
  const fetchNotifications = async () => {
    const userId = localStorage.getItem("user_id");
    if (!userId) return;

    try {
      const res = await fetch(`${API_BASE}/notifications/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchNotifications();
      // Poll for new notifications every 5 minutes
      const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: "PUT"
      });
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const markAllRead = async () => {
    const userId = localStorage.getItem("user_id");
    if (!userId) return;

    try {
      await fetch(`${API_BASE}/notifications/${userId}/read-all`, {
        method: "PUT"
      });
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all read:", error);
    }
  };

  const menuItems = [
    { label: 'Trang chủ', path: '/' },
    { label: 'Theo dõi thai kỳ', path: '/pregnancy-tracker' },
    { label: 'Cộng đồng', path: '/community' },
    { label: 'Lịch hẹn', path: '/appointments' },
    { label: 'Hồ sơ y tế', path: '/medical-records' },
    { label: 'Mua sắm', path: '/shopping' },
  ];

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
              <img
                src={logo}
                alt="Logo"
                className="w-8 h-8 object-contain"
              />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
              AN NHI
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-1">
            {menuItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button variant="ghost" className="text-pink-600 hover:text-pink-700 hover:bg-pink-50 transition-all duration-200 text-lg font-medium">
                  {item.label}
                </Button>
              </Link>
            ))}
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative hover:bg-pink-50">
                  <Bell className="w-7 h-7 text-gray-700" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 bg-pink-500 text-white text-[10px] leading-none">
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="p-2">
                  <h3 className="font-semibold mb-2 text-gray-800">Thông báo</h3>
                  {notifications.slice(0, 3).map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-3 rounded-lg mb-2 cursor-pointer hover:bg-pink-50 transition-colors ${!notif.read ? 'bg-pink-50' : 'bg-white'
                        }`}
                    >
                      <p className="font-medium text-sm text-gray-800">{notif.title}</p>
                      <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-pink-50">
                    <User className="w-7 h-7 text-gray-700" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-3 py-2 border-b mb-2">
                    <div className="font-semibold text-gray-800">{currentUser?.name || 'Người dùng'}</div>
                    <div className="text-xs text-gray-500">{currentUser?.email}</div>
                    {currentUser?.currentWeek && (
                      <div className="mt-1 inline-flex items-center text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                        Tuần {currentUser.currentWeek}
                      </div>
                    )}
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    Hồ sơ cá nhân
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    logout();
                    navigate('/');
                  }}>
                    Đăng xuất
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center space-x-2">
                <Button variant="ghost" onClick={() => navigate('/login')} className="hover:bg-pink-50 hover:text-pink-600">
                  Đăng nhập
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white">
                  Đăng ký
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2 animate-in slide-in-from-top">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button variant="ghost" className="w-full justify-start text-pink-600 hover:text-pink-700 hover:bg-pink-50 font-medium">
                  {item.label}
                </Button>
              </Link>
            ))}
            {!isLoggedIn && (
              <div className="space-y-2 pt-2 border-t">
                <Button
                  variant="ghost"
                  className="w-full justify-start hover:bg-pink-50 hover:text-pink-600"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                >
                  Đăng nhập
                </Button>
                <Button
                  className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white"
                  onClick={() => {
                    navigate('/register');
                    setMobileMenuOpen(false);
                  }}
                >
                  Đăng ký
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;